import { useState } from 'react'
import { ui } from '../styles/ui'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useTranslation } from '../contexts/TranslationContext'

// The canonical pipeline step order for display
const STEP_ORDER = [
  'fetch_html',
  'preprocess',
  'heuristic',
  'selector_parse',
  'llm_parse',
  'validation',
  'ocr',
  'product_analysis',
  'product_translation',
  'qrcode_extraction',
]

const STATUS_CLASSES = {
  completed: { dot: 'bg-green-500', badge: `${ui.badge} ${ui.badgeSuccess}` },
  failed: { dot: 'bg-red-500', badge: `${ui.badge} ${ui.badgeError}` },
  running: { dot: 'bg-yellow-400', badge: `${ui.badge} ${ui.badgeWarning}` },
  pending: { dot: 'bg-gray-300', badge: `${ui.badge} ${ui.badgeNeutral}` },
}

function getStatus(step) {
  return step.status || 'completed'
}

/**
 * Renders the pipeline execution trace for a job.
 * Steps are sorted by STEP_ORDER; any extra steps appear at the end.
 * Each step card can be expanded to show the output or error.
 */
export default function PipelineVisualizer({ steps = [], loading = false }) {
  const [expanded, setExpanded] = useState(new Set())
  const { t } = useTranslation()

  const toggle = (step) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(step)) next.delete(step)
      else next.add(step)
      return next
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className={ui.spinner} />
      </div>
    )
  }

  if (!steps.length) {
    return (
      <div className={`${ui.card} ${ui.cardBody} text-sm text-muted-foreground`}>
        {t('No pipeline data available for this job')}
      </div>
    )
  }

  const sorted = [...steps].sort((a, b) => {
    const ai = STEP_ORDER.indexOf(a.step)
    const bi = STEP_ORDER.indexOf(b.step)
    if (ai === -1 && bi === -1) return 0
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })

  return (
    <div className="space-y-2">
      {sorted.map((step, index) => {
        const status = getStatus(step)
        const cls = STATUS_CLASSES[status] ?? STATUS_CLASSES.pending
        const isOpen = expanded.has(step.step)
        const hasDetail = step.output != null || step.error

        return (
          <div key={step.step} className={`${ui.card} overflow-hidden`}>
            <button
              type="button"
              onClick={() => hasDetail && toggle(step.step)}
              className={`flex w-full items-center gap-3 px-4 py-3 text-left ${hasDetail ? 'hover:bg-surface-elevated cursor-pointer' : 'cursor-default'}`}
            >
              {/* Step index */}
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface-elevated text-xs font-bold text-muted-foreground">
                {index + 1}
              </span>

              {/* Status dot */}
              <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${cls.dot}`} />

              {/* Step name */}
              <span className="flex-1 text-sm font-medium text-foreground">
                {step.step.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </span>

              {/* Status badge */}
              <span className={cls.badge}>{status}</span>

              {/* Duration */}
              {step.durationMs != null && (
                <span className={`${ui.badge} ${ui.badgeNeutral}`}>{step.durationMs} ms</span>
              )}

              {/* Expand icon */}
              {hasDetail && (
                <span className="text-muted-foreground ml-1">
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
              )}
            </button>

            {isOpen && (
              <div className="bg-surface-elevated px-4 pb-4 space-y-3">
                {step.error && (
                  <div>
                    <div className="text-xs font-semibold uppercase text-red-600 mb-1">{t('Error')}</div>
                    <pre className="rounded bg-red-50 p-3 text-xs text-red-800 overflow-x-auto whitespace-pre-wrap">{step.error}</pre>
                  </div>
                )}
                {step.output != null && (
                  <div>
                    <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">{t('Result')}</div>
                    <pre className="rounded bg-surface border border-outline p-3 text-xs text-foreground overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(step.output, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

